import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

type Values = {
  [key: string]: any;
};

type Props = React.PropsWithChildren<{
  values: Values;
  condition?: string;
  name: string;
  multiple?: boolean;
}>;

export const shouldBeVisible = (condition: string, values: Values) => {
  if (!condition) {
    return true;
  }

  const [name, operator, value] = condition.split(':');
  const relatedValue = values[name];

  if (!relatedValue) {
    console.warn('Related field value found: ', name);
    return true;
  }

  switch (operator) {
    case 'eq': {
      return relatedValue === value;
    }
    case 'neq': {
      return relatedValue !== value;
    }
    case 'in': {
      if (typeof relatedValue === 'string') {
        return Array.from(value.split(',') || []).includes(relatedValue);
      }

      break;
    }
    case 'not_in': {
      if (typeof relatedValue === 'string') {
        return !Array.from(value.split(',') || []).includes(relatedValue);
      }

      return true;
    }
    default: {
      throw new Error(`Unsupported operator: ${operator}`);
    }
  }
};

function isAlreadyCleared(current: unknown, clearedValue: null | unknown[]) {
  if (Array.isArray(clearedValue)) {
    return Array.isArray(current) && current.length === 0;
  }

  return current === null || current === undefined || current === '';
}

const EMPTY_ARRAY: unknown[] = [];

const ConditionalFieldCheckerModern: React.FC<Props> = ({
  values,
  condition,
  name,
  multiple = false,
  children,
}) => {
  const { setValue, getValues, register } = useFormContext();
  const clearedValue = useMemo(
    () => (multiple ? EMPTY_ARRAY : null),
    [multiple],
  );

  const isVisible = useMemo(
    () => shouldBeVisible(condition ?? '', values),
    [values, condition],
  );

  useEffect(() => {
    if (isVisible) {
      return;
    }

    register(name);

    const current = getValues(name);
    if (isAlreadyCleared(current, clearedValue)) {
      return;
    }

    setValue(name, clearedValue, {
      shouldValidate: false,
      shouldDirty: true,
      shouldTouch: false,
    });
  }, [isVisible, name, clearedValue, setValue, getValues, register]);

  if (!isVisible) {
    return null;
  }

  return children;
};

export default ConditionalFieldCheckerModern;
